import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material"

export type TranslationDraft = {
  id?: number | null
  language: "en" | "de"
  text: string
  hint: string
  literal?: string
  position?: number
  _destroy?: boolean
}

type Props = {
  translations: TranslationDraft[]
  onChange: (next: TranslationDraft[]) => void
  // When true, exposes a `literal` field per row (sentence-level translations).
  withLiteral?: boolean
  // When false, hides the `hint` field (e.g. AI word workflow — RI #573).
  withHint?: boolean
  testIdPrefix?: string
}

const LANGUAGES: { code: "en" | "de"; label: string }[] = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" }
]

function emptyTranslation(language: "en" | "de"): TranslationDraft {
  return { language, text: "", hint: "", literal: "" }
}

export default function TranslationsEditor({ translations, onChange, withLiteral = false, withHint = true, testIdPrefix = "translation" }: Props) {
  const update = (index: number, patch: Partial<TranslationDraft>) => {
    const next = translations.map((t, i) => (i === index ? { ...t, ...patch } : t))
    onChange(next)
  }

  const remove = (index: number) => {
    const target = translations[index]
    if (target.id) {
      onChange(translations.map((t, i) => (i === index ? { ...t, _destroy: true } : t)))
    } else {
      onChange(translations.filter((_, i) => i !== index))
    }
  }

  const add = (language: "en" | "de") => {
    onChange([...translations, emptyTranslation(language)])
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {LANGUAGES.map((lang) => {
        const rows = translations
          .map((t, idx) => ({ t, idx }))
          .filter((entry) => entry.t.language === lang.code && !entry.t._destroy)
        return (
          <Box key={lang.code} data-testid={`${testIdPrefix}-group-${lang.code}`}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">{lang.label}</Typography>
              <Button size="small" onClick={() => add(lang.code)}
                      data-testid={`${testIdPrefix}-add-${lang.code}`}>+ Add</Button>
            </Stack>
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No {lang.label.toLowerCase()} translations yet.
              </Typography>
            )}
            <Stack gap={1}>
              {rows.map(({ t, idx }) => (
                <Box key={t.id ?? `new-${idx}`}
                     sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ sm: "flex-start" }}>
                    <TextField
                      label="Translation" value={t.text} fullWidth size="small"
                      onChange={(e) => update(idx, { text: e.target.value })}
                      slotProps={{ htmlInput: { "data-testid": `${testIdPrefix}-${lang.code}-text-${rows.indexOf({ t, idx })}` } }}
                    />
                    {withHint && (
                      <TextField
                        label="Hint" value={t.hint ?? ""} fullWidth size="small"
                        onChange={(e) => update(idx, { hint: e.target.value })}
                      />
                    )}
                    {withLiteral && (
                      <TextField
                        label="Literal" value={t.literal ?? ""} fullWidth size="small"
                        onChange={(e) => update(idx, { literal: e.target.value })}
                      />
                    )}
                    <IconButton onClick={() => remove(idx)} aria-label="remove translation"
                                data-testid={`${testIdPrefix}-remove-${lang.code}`}>
                      ×
                    </IconButton>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )
      })}
    </Box>
  )
}

// Helper for FormData submission — appends translations_attributes[i][...] entries.
export function appendTranslationsFormData(
  data: FormData, prefix: string, translations: TranslationDraft[]
) {
  translations.forEach((t, i) => {
    const base = `${prefix}[translations_attributes][${i}]`
    if (t.id) data.append(`${base}[id]`, String(t.id))
    data.append(`${base}[language]`, t.language)
    data.append(`${base}[text]`, t.text)
    data.append(`${base}[hint]`, t.hint ?? "")
    if (t.literal !== undefined) data.append(`${base}[literal]`, t.literal ?? "")
    if (t.position !== undefined) data.append(`${base}[position]`, String(t.position))
    if (t._destroy) data.append(`${base}[_destroy]`, "1")
  })
}
